import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, currency, resolveImage } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Minus, Plus, Check, Leaf, ShieldCheck, Droplets } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products/${slug}`)
      .then((r) => setProduct(r.data))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24 text-center text-[#8a8d86]">
        Loading…
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24 text-center">
        <h2 className="font-serif text-3xl text-[#1e392a]">Product not found.</h2>
        <Link to="/shop" className="mt-4 inline-block text-[#c36a4f] hover:underline">
          Back to shop
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <p className="text-xs text-[#8a8d86] mb-8">
        <Link to="/" className="hover:text-[#1e392a]">Home</Link> /{" "}
        <Link to="/shop" className="hover:text-[#1e392a]">Shop</Link> / {product.name}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="lg:sticky lg:top-24 h-fit">
          <div className="aspect-[4/5] bg-[#efeadd] rounded-md overflow-hidden">
            {product.image_url && (
              <img
                src={resolveImage(product.image_url)}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            )}
          </div>
        </div>

        <div>
          <p className="overline">{product.category}</p>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-[#1e392a] tracking-tight">
            {product.name}
          </h1>
          <p className="mt-4 text-base text-[#1a1b1a]/70 leading-relaxed">
            {product.short_description}
          </p>

          <div className="mt-6 flex items-baseline gap-3">
            <span data-testid="pd-price" className="font-serif text-4xl text-[#1e392a]">
              {currency(product.price)}
            </span>
            {product.compare_at_price && (
              <span className="text-lg text-[#8a8d86] line-through">
                {currency(product.compare_at_price)}
              </span>
            )}
          </div>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-[#e6e2d8] rounded-full bg-white">
              <button
                data-testid="pd-qty-dec"
                onClick={() => setQty((v) => Math.max(1, v - 1))}
                className="p-3 hover:bg-[#efeadd] rounded-l-full"
              >
                <Minus className="h-3 w-3" />
              </button>
              <span data-testid="pd-qty-value" className="px-4 text-sm">{qty}</span>
              <button
                data-testid="pd-qty-inc"
                onClick={() => setQty((v) => v + 1)}
                className="p-3 hover:bg-[#efeadd] rounded-r-full"
              >
                <Plus className="h-3 w-3" />
              </button>
            </div>
            <Button
              data-testid="pd-add-to-cart"
              onClick={() => addItem(product, qty)}
              className="flex-1 h-12 rounded-full bg-[#1e392a] hover:bg-[#14251b] text-white"
            >
              Add to cart · {currency(product.price * qty)}
            </Button>
          </div>

          <div className="mt-6 flex flex-wrap gap-4 text-xs text-[#8a8d86]">
            <span className="flex items-center gap-1.5">
              <Leaf className="h-3.5 w-3.5 text-[#c36a4f]" strokeWidth={1.5} /> Cold-pressed
            </span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-[#c36a4f]" strokeWidth={1.5} /> Dermatologically tested
            </span>
            <span className="flex items-center gap-1.5">
              <Droplets className="h-3.5 w-3.5 text-[#c36a4f]" strokeWidth={1.5} /> Glass bottled
            </span>
          </div>

          <div className="mt-12">
            <p className="overline mb-3">Key Benefits</p>
            <ul className="space-y-2">
              {product.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-[#c36a4f] mt-0.5" strokeWidth={2} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <Accordion type="single" collapsible className="mt-8">
            <AccordionItem value="desc" className="border-[#e6e2d8]">
              <AccordionTrigger data-testid="pd-desc-trigger" className="font-serif text-xl text-[#1e392a]">
                Description
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[#1a1b1a]/70 leading-relaxed">
                {product.description}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="ingredients" className="border-[#e6e2d8]">
              <AccordionTrigger data-testid="pd-ing-trigger" className="font-serif text-xl text-[#1e392a]">
                Ingredients
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[#1a1b1a]/70 leading-relaxed">
                {product.ingredients}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="shipping" className="border-[#e6e2d8]">
              <AccordionTrigger data-testid="pd-ship-trigger" className="font-serif text-xl text-[#1e392a]">
                Shipping & Returns
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[#1a1b1a]/70 leading-relaxed">
                Free shipping across India on orders over ₹999. Dispatched within 24
                hours. Not satisfied? Return within 14 days for a full refund.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
}
